import { useEffect, useRef } from 'react';

import BannerImpressionEvent from '~/events/BannerImpressionEvent';

type ImpressionOptions = {
  bannerId: string;
  bannerName: string;
  bannerPosition: string;
};

function useImpressions(banners: ImpressionOptions[]) {
  const bannerRefs = useRef<(HTMLDivElement | null)[]>(
    new Array(banners.length).fill(null)
  );
  const hasTracked = useRef<boolean[]>(new Array(banners.length).fill(false));

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    banners.forEach((banner, index) => {
      if (hasTracked.current[index]) {
        return;
      }

      const element = bannerRefs.current[index];
      if (!element) {
        return;
      }

      const trackImpression = () => {
        if (hasTracked.current[index]) {
          return;
        }

        new BannerImpressionEvent({
          banner_id: banner.bannerId,
          banner_name: banner.bannerName,
          banner_position: banner.bannerPosition,
          isBot: false
        }).sendToGtm();

        hasTracked.current[index] = true;
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              trackImpression();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.5 }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [banners]);

  return banners.map((_, index) => (element: HTMLDivElement | null) => {
    bannerRefs.current[index] = element;
  });
}

export default useImpressions;
